import React from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Box, 
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useForm } from 'react-hook-form';
import dayjs, { Dayjs } from 'dayjs';
import BasicInfoSection from './BasicInfoSection';
import ContactFieldsSection from './ContactFieldsSection';
import AdditionalInfoSection from './AdditionalInfoSection';

export interface PersonFormData {
  name: string;
  email: string;
  phone: string;
  preferredMethod: string;
  birthday: Dayjs | null;
  city: string;
  country: string;
  notes: string;
}

interface PersonDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: PersonFormData) => Promise<void>;
  initialData?: any;
  title: string;
}

const contactMethods = [
  { id: 'email', label: 'Email' },
  { id: 'phone', label: 'Phone' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'signal', label: 'Signal' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'matrix', label: 'Matrix' },
];

const PersonDialog: React.FC<PersonDialogProps> = ({ open, onClose, onSubmit, initialData, title }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { control, handleSubmit, reset } = useForm<PersonFormData>({
    defaultValues: {
      name: initialData?.name || '',
      email: initialData?.email || '',
      phone: initialData?.phone || '',
      preferredMethod: initialData?.preferredMethod || 'email',
      birthday: initialData?.birthday ? dayjs(initialData.birthday) : null,
      city: initialData?.city || '',
      country: initialData?.country || '',
      notes: initialData?.notes || '',
    }
  });

  React.useEffect(() => {
    if (open) {
      reset({
        name: initialData?.name || '',
        email: initialData?.email || '',
        phone: initialData?.phone || '',
        preferredMethod: initialData?.preferredMethod || 'email',
        birthday: initialData?.birthday ? dayjs(initialData.birthday) : null,
        city: initialData?.city || '',
        country: initialData?.country || '',
        notes: initialData?.notes || '',
      });
    }
  }, [open, initialData, reset]);

  const onFormSubmit = async (data: PersonFormData) => {
    onClose();
    onSubmit(data).catch((err) => {
      console.error('Failed to save person:', err);
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>{title}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <BasicInfoSection control={control} contactMethods={contactMethods} />

            <ContactFieldsSection control={control} />

            <Divider />

            <AdditionalInfoSection control={control} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit(onFormSubmit)}>
            Save Person
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default PersonDialog;
